import { format, formatDistanceToNow, isAfter, addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { vi, enUS } from "date-fns/locale";

const LOCALE_MAP = { vi, en: enUS } as const;

// M-13 FIX: Added optional locale parameter so Vietnamese storefront
// renders month names in Vietnamese instead of always English.
export function formatDate(date: Date | string, timezone?: string, locale?: "vi" | "en"): string {
  const opts = locale ? { locale: LOCALE_MAP[locale] } : undefined;
  if (timezone) {
    return formatInTimeZone(new Date(date), timezone, "MMM d, yyyy", opts);
  }
  return format(new Date(date), "MMM d, yyyy", opts);
}

export function formatDateTime(date: Date | string, timezone?: string, locale?: "vi" | "en"): string {
  const opts = locale ? { locale: LOCALE_MAP[locale] } : undefined;
  if (timezone) {
    return formatInTimeZone(new Date(date), timezone, "MMM d, yyyy h:mm a", opts);
  }
  return format(new Date(date), "MMM d, yyyy h:mm a", opts);
}

export function timeAgo(date: Date | string): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}

export function isOverdue(dueDate: Date | string): boolean {
  return isAfter(new Date(), new Date(dueDate));
}

/**
 * SSR-safe date formatter for storefront pages.
 *
 * Why this exists: bare `new Date(d).toLocaleDateString(locale)` calls
 * differ between server (Node, UTC on Fly's SIN region) and client
 * (browser, typically Asia/Ho_Chi_Minh = UTC+7) because the runtime
 * default timezone differs. A review created at 17:00 UTC on May 7
 * formats as "May 7" on the server and "May 8" on the client → React
 * detects the text mismatch during hydration, abandons the affected
 * subtree, and re-renders client-only — generating ~40 cascading
 * #418/#425 hydration warnings on every PDP load.
 *
 * Pinning timezone to "Asia/Ho_Chi_Minh" (the merchant's primary
 * market) keeps SSR + CSR byte-identical AND keeps the
 * customer-perceived calendar date correct for the dominant audience.
 *
 * Use this helper instead of bare toLocaleDateString in any component
 * that renders a server-loader-provided date.
 */
const STOREFRONT_TZ = "Asia/Ho_Chi_Minh";

export function formatStorefrontDate(
  date: Date | string,
  locale: "vi" | "en" = "vi",
  options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" },
): string {
  const intlLocale = locale === "vi" ? "vi-VN" : "en-US";
  return new Date(date).toLocaleDateString(intlLocale, {
    ...options,
    timeZone: STOREFRONT_TZ,
  });
}

export function addBusinessDays(date: Date, days: number): Date {
  let result = new Date(date);
  let remaining = Math.abs(days);
  const direction = days >= 0 ? 1 : -1;
  while (remaining > 0) {
    result = addDays(result, direction);
    const day = result.getDay();
    if (day !== 0 && day !== 6) remaining--;
  }
  return result;
}
